import * as React from 'react';

import {
  DimeResizableHandle,
  DimeResizablePanel,
  DimeResizablePanelGroup,
} from '../DimeResizable.js';
import { useIsMobile } from '../hooks/use-mobile.js';
import { DETAIL_LAYOUT_PANE_SIZES } from '../lib/detail-layout-pane-sizes.js';
import { useDetailLayoutFolds } from '../lib/use-detail-layout-folds.js';
import { useDetailLayoutPanels } from '../lib/use-detail-layout-panels.js';
import { useDetailLayoutTab } from '../lib/use-detail-layout-tab.js';
import { useElementWidth } from '../lib/use-element-width.js';
import { cn } from '../lib/utils.js';
import type {
  DimeDetailLayoutHeader,
  DimeDetailLayoutPane,
  DimeDetailLayoutPaneCard,
  DimeDetailLayoutTab,
  DimeDetailLayoutToolbar,
} from './DimeDetailLayout.types.js';
import { DimeDetailLayoutActionBar } from './DimeDetailLayoutActionBar.js';
import { DimeDetailLayoutLeftPaneSheet } from './DimeDetailLayoutLeftPaneSheet.js';
import { DimeDetailLayoutRecord } from './DimeDetailLayoutRecord.js';
import { DimeDetailLayoutRightPaneCard } from './DimeDetailLayoutRightPaneCard.js';

type DimeDetailLayoutProps = Readonly<{
  toolbar: DimeDetailLayoutToolbar;
  /** Resizable pane on wide layouts, left sheet once the layout is too narrow to fit it; omit for no left-pane toggle. */
  leftPane?: DimeDetailLayoutPane;
  header: DimeDetailLayoutHeader;
  tabs: DimeDetailLayoutTab[];
  activeTab: string;
  onTabChange: (value: string) => void;
  renderTab: (value: string) => React.ReactNode;
  rightPane?: DimeDetailLayoutPaneCard[];
  detailsTabLabel?: string;
  defaultRightPaneOpen?: boolean;
  /** `tab` keeps the right-pane cards in the Details tab at every width, for a record too light for a side pane. */
  rightPanePlacement?: 'side' | 'tab';
  /** Persists panel sizes under this id; omit to keep them for the visit only. */
  autoSaveId?: string;
  className?: string;
}>;

export function DimeDetailLayout({
  toolbar,
  leftPane,
  header,
  tabs,
  activeTab,
  onTabChange,
  renderTab,
  rightPane = [],
  detailsTabLabel = 'Details',
  defaultRightPaneOpen = true,
  rightPanePlacement = 'side',
  autoSaveId,
  className,
}: DimeDetailLayoutProps) {
  const isMobile = useIsMobile();
  const [rootRef, width] = useElementWidth<HTMLDivElement>();
  const [leftPaneOpen, setLeftPaneOpen] = React.useState(leftPane?.defaultOpen ?? false);
  const [rightPaneOpen, setRightPaneOpen] = React.useState(defaultRightPaneOpen);

  const closeLeftPane = React.useCallback(() => setLeftPaneOpen(false), []);
  const closeRightPane = React.useCallback(() => setRightPaneOpen(false), []);
  const hasRightPane = rightPane.length > 0;
  const rightPaneBeside = rightPanePlacement === 'side';
  // Folds are measured on the layout rather than the viewport, so an open sidebar behaves like tablet.
  const { roomForRightPane, rightPaneVisible, leftPaneAsSheet, measured } = useDetailLayoutFolds({
    isMobile,
    width,
    rightPaneWanted: hasRightPane && rightPaneBeside && rightPaneOpen,
    onLeftPaneFold: closeLeftPane,
  });

  const rightPaneFolded = hasRightPane && !rightPaneVisible;
  const leftPaneVisible = Boolean(leftPane) && leftPaneOpen && !leftPaneAsSheet;
  const { defaultLayout, onLayoutChanged, leftPanelRef, rightPanelRef } = useDetailLayoutPanels({
    autoSaveId,
    leftPaneVisible,
    rightPaneVisible,
    onLeftPaneDragShut: closeLeftPane,
    onRightPaneDragShut: closeRightPane,
  });

  const { visibleTabs, currentTab } = useDetailLayoutTab({
    tabs,
    activeTab,
    onTabChange,
    rightPaneFolded,
    measured: measured || !rightPaneBeside,
    detailsTabLabel,
  });

  const cards = rightPane.map((card) => (
    <DimeDetailLayoutRightPaneCard key={card.key} card={card} />
  ));

  const record = (
    <DimeDetailLayoutRecord
      header={header}
      tabs={visibleTabs}
      currentTab={currentTab}
      onTabChange={onTabChange}
      renderTab={renderTab}
      cards={cards}
      isMobile={isMobile}
    />
  );

  const leftPaneSection = leftPane ? (
    <section aria-label={leftPane.title} className="flex h-full min-w-0 flex-col">
      {leftPane.content}
    </section>
  ) : null;

  const actionBar = (
    <DimeDetailLayoutActionBar
      toolbar={toolbar}
      mobile={isMobile}
      leftPaneTitle={leftPane?.title}
      leftPaneOpen={leftPaneOpen}
      leftPanePressed={!leftPaneAsSheet && leftPaneOpen}
      onToggleLeftPane={() => setLeftPaneOpen((open) => !open)}
      showRightPaneToggle={hasRightPane && rightPaneBeside && roomForRightPane}
      rightPaneOpen={rightPaneOpen}
      onToggleRightPane={() => setRightPaneOpen((open) => !open)}
    />
  );
  return (
    <div ref={rootRef} className={cn('flex min-h-0 flex-1 flex-col -m-4', className)}>
      {isMobile ? null : actionBar}
      {isMobile ? (
        <div className="min-h-0 flex-1">{record}</div>
      ) : (
        <DimeResizablePanelGroup
          orientation="horizontal"
          defaultLayout={defaultLayout}
          onLayoutChanged={onLayoutChanged}
          className="min-h-0 flex-1"
        >
          {leftPaneVisible ? (
            <>
              <DimeResizablePanel
                id="left"
                panelRef={leftPanelRef}
                defaultSize={DETAIL_LAYOUT_PANE_SIZES.left.defaultSize}
                minSize={DETAIL_LAYOUT_PANE_SIZES.left.minSize}
                maxSize={DETAIL_LAYOUT_PANE_SIZES.left.maxSize}
                collapsible
                collapsedSize={0}
                className="border-r"
              >
                {leftPaneSection}
              </DimeResizablePanel>
              <DimeResizableHandle withHandle />
            </>
          ) : null}
          <DimeResizablePanel id="record" minSize={DETAIL_LAYOUT_PANE_SIZES.record.minSize}>
            {record}
          </DimeResizablePanel>
          {rightPaneVisible ? (
            <>
              <DimeResizableHandle withHandle />
              <DimeResizablePanel
                id="right"
                panelRef={rightPanelRef}
                defaultSize={DETAIL_LAYOUT_PANE_SIZES.right.defaultSize}
                minSize={DETAIL_LAYOUT_PANE_SIZES.right.minSize}
                maxSize={DETAIL_LAYOUT_PANE_SIZES.right.maxSize}
                collapsible
                collapsedSize={0}
                className="border-l"
              >
                <aside
                  aria-label={detailsTabLabel}
                  className="flex h-full flex-col gap-4 overflow-x-hidden overflow-y-auto p-4 *:shrink-0"
                >
                  {cards}
                </aside>
              </DimeResizablePanel>
            </>
          ) : null}
        </DimeResizablePanelGroup>
      )}
      {isMobile ? actionBar : null}
      {leftPane && leftPaneAsSheet ? (
        <DimeDetailLayoutLeftPaneSheet
          title={leftPane.title}
          open={leftPaneOpen}
          onOpenChange={setLeftPaneOpen}
        >
          {leftPaneSection}
        </DimeDetailLayoutLeftPaneSheet>
      ) : null}
    </div>
  );
}
